import { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close,
  CloudUpload,
  Download,
  FileUpload,
  Schedule,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useBulkUploadSettlementDaysMutation } from '../services/bankApi';

interface BulkSettlementDaysDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface UploadResult {
  success: boolean;
  message: string;
  processed?: number;
  inserted?: number;
  errors?: string[];
}

export default function BulkSettlementDaysDialog({
  open,
  onClose,
  onSuccess,
}: BulkSettlementDaysDialogProps) {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [remarks, setRemarks] = useState('');
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);

  const [bulkUpload, { isLoading: isUploading }] =
    useBulkUploadSettlementDaysMutation();

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setSelectedFile(file);
        setUploadResult(null);
      }
    },
    []
  );

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setRemarks('');
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      setUploadResult({ success: false, message: 'Please select a CSV file to upload' });
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    if (remarks.trim()) {
      formData.append('remarks', remarks.trim());
    }

    try {
      const result = await bulkUpload(formData).unwrap();
      setUploadResult({
        success: true,
        message: result.message,
        processed: result.data?.processed,
        inserted: result.data?.inserted,
        errors: result.data?.errors,
      });
      setSelectedFile(null);
      setRemarks('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onSuccess?.();
    } catch (error: any) {
      setUploadResult({
        success: false,
        message: error?.data?.message || 'Upload failed. Please try again.',
      });
    }
  }, [selectedFile, remarks, bulkUpload, onSuccess]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleDownloadSample = useCallback(() => {
    const csvContent = [
      'gateway,ifsc,trtype,paymode,merchantId,settlementDays,remarks',
      'Billdesk1,KKBK,Lumpsum,Net Banking,MERCH001,T+1,Kotak Billdesk1 lumpsum NB',
      'Billdesk1,KKBK,Hybrid,Net Banking,MERCH001,T+1,Kotak Billdesk1 hybrid NB',
      'Billdesk1,KKBK,Emandate,Net Banking,MERCH001,T+2,Kotak Billdesk1 emandate NB',
      'Billdesk1,KKBK,Emandate,Debit Card,MERCH001,T+2,Kotak Billdesk1 emandate DC',
      'Billdesk1,KKBK,Emandate,Aadhar,MERCH001,T+2,Kotak Billdesk1 emandate Aadhar',
      'Billdesk2,KKBK,Lumpsum,Net Banking,MERCH001,T+1,Kotak Billdesk2 lumpsum NB',
      'Billdesk2,KKBK,Lumpsum,Debit Card,MERCH001,T+1,Kotak Billdesk2 lumpsum DC',
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'settlement_days_sample.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Schedule sx={{ color: 'info.main', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                Bulk Upload Settlement Days
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Upload a CSV to update per-paymode settlement days for one or more merchants
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Expected columns: gateway, ifsc, trtype, paymode, merchantId, settlementDays, remarks
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleDownloadSample}
              sx={{ borderRadius: 2 }}
            >
              Download Sample CSV
            </Button>
          </Box>

          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              p: 4,
              border: '2px dashed',
              borderColor: selectedFile ? 'primary.main' : alpha(theme.palette.grey[400], 0.5),
              borderRadius: 2,
              bgcolor: selectedFile
                ? alpha(theme.palette.primary.main, 0.05)
                : alpha(theme.palette.grey[50], 0.5),
              textAlign: 'center',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.05),
              },
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 500 }}>
              {selectedFile ? selectedFile.name : 'Click to upload CSV file'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedFile
                ? `${(selectedFile.size / 1024).toFixed(1)} KB`
                : 'Only .csv files are supported'}
            </Typography>
          </Box>

          <TextField
            label="Remarks (Optional)"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Enter any remarks about this upload"
            multiline
            rows={2}
            fullWidth
          />

          {uploadResult && (
            <Alert
              severity={uploadResult.success ? 'success' : 'error'}
              sx={{ borderRadius: 2 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                {uploadResult.message}
              </Typography>
              {uploadResult.success && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  {typeof uploadResult.processed === 'number' && (
                    <Chip
                      size="small"
                      label={`${uploadResult.processed} processed`}
                      color="info"
                    />
                  )}
                  {typeof uploadResult.inserted === 'number' && (
                    <Chip
                      size="small"
                      label={`${uploadResult.inserted} inserted`}
                      color="success"
                    />
                  )}
                </Box>
              )}
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="error">
                    Errors: {uploadResult.errors.join(', ')}
                  </Typography>
                </Box>
              )}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          color="info"
          disabled={!selectedFile || isUploading}
          startIcon={isUploading ? <CircularProgress size={20} /> : <FileUpload />}
          sx={{ borderRadius: 2 }}
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
